import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { usePeriodOverPeriod } from "app/components/duration-selector/period-over-period/usePeriodOverPeriod";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import DomainCompareSelector from "app/pages/website-analysis/website-structure/pages/LineOfBusiness/components/DomainCompareSelector/DomainCompareSelector";
import type { ISimilarInfoItem } from "app/services/sitesService/types";
import { allTrackers } from "app/services/track/track";

import { getDomainsDataFromKeys } from "../../../search/overview/helpers/helpers";

import GrowthDriversTable from "./components/GrowthDriversTable/GrowthDriversTable";
import MarketingFocusTable from "./components/MarkteingFocusTable/MarketingFocusTable";
import PromoteCompareBanner from "./components/PromoteCompareBanner/PromoteCompareBanner";
import useMarketingChannelsPresets from "./hooks/useMarketingChannelsPresets";
import useSubtitleFilters from "./hooks/useSubtitleFilters";
import { getComparisonParams } from "./tests/helpers";

const MAX_SIMILAR_SITES = 2;
const GAP_SIZE = "24px";
const TABLE_HEIGHT = "231px";

const DomainsSelectorComponent = styled(Flex)`
    padding: 8px 16px;
    justify-content: space-between;
    border-top: 1px solid ${colorsPalettes.carbon[50]};
`;

const PromoteMarketingChannelsPresets = () => {
    const { params } = useCurrentStateAndParams();
    const { labelKey, periodOverPeriodCompareTypes } = usePeriodOverPeriod();
    const growthDriversSubtitleTriggers = useSelectorTrigger("mmx-growth-drivers");
    const marketingFocusSubtitleTriggers = useSelectorTrigger("mmx-marketing-focus");

    const { isPoP, resolvedDuration, defaultDuration } = getComparisonParams(
        labelKey,
        periodOverPeriodCompareTypes,
        params.comparedDuration,
    );

    const { subTitleFilters: growthDriversSubTitleFilters } = useSubtitleFilters(
        resolvedDuration || defaultDuration,
        growthDriversSubtitleTriggers.selectorHandlers,
    );
    const { subTitleFilters: marketingFocusSubTitleFilters } = useSubtitleFilters(
        resolvedDuration || defaultDuration,
        marketingFocusSubtitleTriggers.selectorHandlers,
    );

    const similarSites = useMemo(
        () => chosenSites.similarSitesData().slice(0, MAX_SIMILAR_SITES) as ISimilarInfoItem[],
        [],
    );

    const hasNoSimilarSites = !similarSites || similarSites.length === 0;

    const domainsKeysWithSimilarSites = useMemo(() => {
        const similarSitesWithKey = similarSites.map((site) => site.Domain).join(",");
        return `${params.key},${similarSitesWithKey}`;
    }, [similarSites, params.key]);

    const domainsData = useMemo(
        () => getDomainsDataFromKeys(domainsKeysWithSimilarSites, similarSites),
        [domainsKeysWithSimilarSites, similarSites],
    );

    const { absoluteData, percentageData, isLoading, domains } = useMarketingChannelsPresets(resolvedDuration, {
        domainsKeys: domainsKeysWithSimilarSites,
        domainsData,
    });

    const sites = domains.map((domain) => ({ name: domain.name, icon: domain.favicon }));

    const footerComponent = useMemo(() => {
        if (sites.length === 0) {
            return null;
        }

        const handleCompareClick = () => {
            allTrackers.dispatchTrackEvent("Button", "click", `promote compare/${params.key}/heatmap`);
        };
        return (
            <DomainCompareSelector
                sites={sites}
                domainsArr={domainsKeysWithSimilarSites}
                maxSuggestedSites={MAX_SIMILAR_SITES}
                container={DomainsSelectorComponent}
                buttonVariant="flat"
                iconName="add"
                onClick={handleCompareClick}
            />
        );
    }, [sites, domainsKeysWithSimilarSites]);

    const commonTableProps = useMemo(
        () => ({
            isLoading,
            domains,
            isPoP,
            footerComponent,
            withOpacity: true,
            tableHeight: TABLE_HEIGHT,
        }),
        [isLoading, domains, isPoP, footerComponent],
    );

    const hasNoData =
        !isLoading && (!absoluteData || absoluteData.length === 0) && (!percentageData || percentageData.length === 0);

    if (hasNoSimilarSites) {
        return null;
    }

    if (hasNoData) {
        return <PromoteCompareBanner />;
    }

    return (
        <Flex gap={GAP_SIZE}>
            <GrowthDriversTable
                data={absoluteData}
                subTitleFilters={growthDriversSubTitleFilters}
                {...commonTableProps}
            />
            <MarketingFocusTable
                data={percentageData}
                subTitleFilters={marketingFocusSubTitleFilters}
                {...commonTableProps}
            />
        </Flex>
    );
};

export default PromoteMarketingChannelsPresets;
